import { Client, Language } from '../enums/common';
import { isiOSDevice } from './common';
import { getCookieHelpers } from './cookie';

declare global {
    interface Window {
        IxiWebView?: {
            openBrowser?: (url: string) => void;
            pwaReady?: () => void;
            quit?: () => void;
            triggerBridgeEvent?: (eventName: string, data: string) => void;
        };
        webkit?: {
            messageHandlers?: {
                appInterface: {
                    postMessage: (data: string) => void;
                };
                callbackHandler: {
                    postMessage: (data: { openSafari: string }) => void;
                };
            };
        };
    }
}

export const isForeignEnvironment = (clientId?: Client) => {
    const clientid = clientId || getCookieHelpers().getCookie('clientId');
    return ![Client.IXIWEB, Client.IXIMWEB, Client.IXIMWEBIC, Client.IXIWEBIC].includes(clientid as Client);
};

export const openBrowser = (url: string) => {
    if (typeof window === 'undefined') return false;

    if (isForeignEnvironment()) {
        if (isiOSDevice()) {
            if (url.indexOf('http') !== 0 && url.indexOf('ixigo://') !== 0 && url.indexOf('ixigotrains://') !== 0) {
                url = `${window.location.hostname}${url}`;
            }
            if (window.webkit?.messageHandlers?.callbackHandler) {
                window.webkit.messageHandlers.callbackHandler.postMessage({
                    openSafari: url,
                });
            }
        } else if (window.IxiWebView && typeof window.IxiWebView.openBrowser === 'function') {
            window.IxiWebView.openBrowser(url);
        }

        return true;
    }

    return false;
};

export const openExternalLink = (url: string) => {
    if (typeof window === 'undefined') return;

    if (!url.includes('http')) {
        const { host, protocol } = window.location;
        url = `${protocol}//${host}${url}`;
    }

    if (isForeignEnvironment()) {
        const isSupported = openBrowser(url);
        if (!isSupported && window) {
            window.open(url);
        }
    } else {
        window.open(url);
    }
};

export const pwaReady = () => {
    if (!isForeignEnvironment()) return;

    if (window.webkit?.messageHandlers?.appInterface) {
        window.webkit.messageHandlers.appInterface.postMessage('pwaReady');
        return;
    }

    if (typeof window.IxiWebView?.pwaReady === 'function') {
        window.IxiWebView.pwaReady();
    }
};

export const pwaQuit = () => {
    if (!isForeignEnvironment() || getCookieHelpers().getCookie('clientId') === Client.CTMWEB) {
        if (typeof window !== 'undefined') {
            window.history.back();
        }
        return;
    }

    if (window.webkit?.messageHandlers?.appInterface) {
        window.webkit.messageHandlers.appInterface.postMessage('quit');
        return;
    }

    if (typeof window.IxiWebView?.quit === 'function') {
        window.IxiWebView.quit();
    }
};

export const shouldPwaQuit = () => {
    if (typeof window === 'undefined') return false;

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('entryPoint') === 'webview') return false;

    return true;
};

export const taraPwaHelper = (() => {
    const isValidPwa =
        typeof window !== 'undefined' &&
        getCookieHelpers().getCookie('clientId') === Client.IXIMAAD &&
        typeof window.IxiWebView?.triggerBridgeEvent === 'function';

    const trigger = (
        eventName: string,
        config: {
            enableActions: boolean;
            playVideo: string | null;
            showIntroduction: boolean;
            showTooltip: boolean;
            showVideo: boolean;
            submit: string;
            lang?: Language;
            metaData?: Record<string, string | null>;
            mode?: 'SPEECH' | 'TEXT';
        },
    ) => {
        if (!isValidPwa) return;

        const searchParams = new URLSearchParams(window.location.search);

        config.metaData = {
            bookingId: searchParams.get('bookingId'),
            conversationId: searchParams.get('id'),
            product: searchParams.get('product'),
            lang: config.lang || Language.EN,
        };

        window.IxiWebView!.triggerBridgeEvent!(eventName, JSON.stringify(config));
    };

    return { isValidPwa, trigger };
})();
